import type { PrimarySku, SkuDisplayOptions } from '@/composables/use-sku-display/types'
import type { SkuCodeModel } from '@/domains/products/types/models/SkuCodeModel.types'

import { SKU_FORMAT } from '@/domains/products/types/models/SkuCodeModel.types'

/**
 * Composable for consistent SKU code display across the application
 *
 * @example
 * ```ts
 * const { formatSingleSku, formatSkuSummary } = useSkuDisplay()
 *
 * // Display primary SKU with type
 * const display = formatSingleSku(variant.sku_codes) // "SUPREF: ABC123"
 *
 * // Display with count
 * const summary = formatSkuSummary(variant.sku_codes) // "SUPREF: ABC123 +2"
 * ```
 */
export const useSkuDisplay = () => {
  /**
   * Sort SKU codes to prioritize SupRef type
   */
  const sortSkuCodes = (skuCodes: SkuCodeModel[]): SkuCodeModel[] => {
    return [...skuCodes].sort((a, b) => {
      if (a.type === SKU_FORMAT.SupRef) return -1
      if (b.type === SKU_FORMAT.SupRef) return 1
      return 0
    })
  }

  /**
   * Get the primary SKU code (prioritizing SupRef type)
   *
   * @param skuCodes - Array of SKU codes
   * @returns The primary SKU or null if none exist
   */
  const getPrimarySku = (skuCodes: SkuCodeModel[] | undefined | null): PrimarySku | null => {
    if (!skuCodes || skuCodes.length === 0) {
      return null
    }

    const sorted = sortSkuCodes(skuCodes)
    const primary = sorted[0]

    return {
      type: primary.type,
      code: primary.code,
      raw: primary
    }
  }

  /**
   * Format a single SKU code (primary/first SKU)
   *
   * @param skuCodes - Array of SKU codes
   * @param options - Display options
   * @returns Formatted SKU string or empty string if none exist
   *
   * @example
   * ```ts
   * formatSingleSku(skuCodes) // "SUPREF: ABC123"
   * formatSingleSku(skuCodes, { showType: false }) // "ABC123"
   * ```
   */
  const formatSingleSku = (skuCodes: SkuCodeModel[] | undefined | null, options: SkuDisplayOptions = {}): string => {
    const { showType = true, typeSeparator = ': ', prioritizeSupRef = true } = options

    if (!skuCodes || skuCodes.length === 0) {
      return ''
    }

    const codes = prioritizeSupRef ? sortSkuCodes(skuCodes) : skuCodes
    const primary = codes[0]

    return showType ? `${primary.type}${typeSeparator}${primary.code}` : primary.code
  }

  /**
   * Format all SKU codes
   *
   * @param skuCodes - Array of SKU codes
   * @param options - Display options
   * @returns Formatted string with all SKUs
   *
   * @example
   * ```ts
   * formatAllSkus(skuCodes) // "SUPREF: ABC123, EAN: 1234567890123, UPC: 123456789012"
   * formatAllSkus(skuCodes, { showType: false }) // "ABC123, 1234567890123, 123456789012"
   * ```
   */
  const formatAllSkus = (skuCodes: SkuCodeModel[] | undefined | null, options: SkuDisplayOptions = {}): string => {
    const { showType = true, typeSeparator = ': ', skuSeparator = ', ', prioritizeSupRef = true } = options

    if (!skuCodes || skuCodes.length === 0) {
      return ''
    }

    const codes = prioritizeSupRef ? sortSkuCodes(skuCodes) : skuCodes

    return codes.map(sku => (showType ? `${sku.type}${typeSeparator}${sku.code}` : sku.code)).join(skuSeparator)
  }

  /**
   * Format SKU with count indicator (e.g., "SUPREF: ABC123 +2")
   * Shows the primary SKU and indicates how many additional SKUs exist
   *
   * @param skuCodes - Array of SKU codes
   * @param options - Display options
   * @returns Formatted string with primary SKU and count
   *
   * @example
   * ```ts
   * formatSkuSummary(skuCodes) // "SUPREF: ABC123 +2"
   * formatSkuSummary(skuCodes, { showType: false }) // "ABC123 +2"
   * ```
   */
  const formatSkuSummary = (skuCodes: SkuCodeModel[] | undefined | null, options: SkuDisplayOptions = {}): string => {
    const {
      showType = true,
      typeSeparator = ': ',
      prioritizeSupRef = true,
      overflowFormat = (count: number) => ` +${count}`
    } = options

    if (!skuCodes || skuCodes.length === 0) {
      return ''
    }

    const codes = prioritizeSupRef ? sortSkuCodes(skuCodes) : skuCodes
    const primary = codes[0]
    const remainingCount = codes.length - 1

    const primaryDisplay = showType ? `${primary.type}${typeSeparator}${primary.code}` : primary.code

    return remainingCount > 0 ? `${primaryDisplay}${overflowFormat(remainingCount)}` : primaryDisplay
  }

  /**
   * Format only the SKU codes without type prefixes
   * Useful for compact displays
   *
   * @param skuCodes - Array of SKU codes
   * @param options - Display options
   * @returns Formatted string with only codes
   *
   * @example
   * ```ts
   * formatSkuCodesOnly(skuCodes) // "ABC123, 1234567890123, 123456789012"
   * ```
   */
  const formatSkuCodesOnly = (skuCodes: SkuCodeModel[] | undefined | null, options: SkuDisplayOptions = {}): string => {
    return formatAllSkus(skuCodes, { ...options, showType: false })
  }

  /**
   * Format SKUs for labels/badges (compact format)
   * Useful for inline displays with limited space
   *
   * @param skuCodes - Array of SKU codes
   * @returns Formatted string optimized for labels
   *
   * @example
   * ```ts
   * formatSkuLabel(skuCodes) // "SUPREF: ABC123"
   * ```
   */
  const formatSkuLabel = (skuCodes: SkuCodeModel[] | undefined | null): string => {
    return formatSingleSku(skuCodes, {
      showType: true,
      typeSeparator: ': ',
      prioritizeSupRef: true
    })
  }

  /**
   * Check if variant has any SKU codes
   *
   * @param skuCodes - Array of SKU codes
   * @returns True if SKU codes exist
   */
  const hasSkuCodes = (skuCodes: SkuCodeModel[] | undefined | null): boolean => {
    return !!skuCodes && skuCodes.length > 0
  }

  /**
   * Get the count of SKU codes
   *
   * @param skuCodes - Array of SKU codes
   * @returns Number of SKU codes
   */
  const getSkuCount = (skuCodes: SkuCodeModel[] | undefined | null): number => {
    return skuCodes?.length ?? 0
  }

  return {
    // Core formatting functions
    formatSingleSku,
    formatAllSkus,
    formatSkuSummary,
    formatSkuCodesOnly,
    formatSkuLabel,

    // Helper functions
    getPrimarySku,
    hasSkuCodes,
    getSkuCount,
    sortSkuCodes
  }
}
